import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, DatePicker, Typography, Card, Avatar, Tag, message, Form } from 'antd';
import { EditOutlined, ClockCircleOutlined, SaveOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { all_routes } from '../../../router/all_routes';
import dailyRecordService from '../../../services/dailyRecord/dailyRecord.service';
import { ForgottenCheckoutDto } from '../../../types/user.types'; // ForgottenCheckoutDto tipini ekle veya tanımla
import { handleApiError } from '../../../utils/errorHandler';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import FooterSection from '../../shared/components/footerSection';
import { getFileUrl } from '../../../utils/file.utils';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/config';
import datePickerTr from 'antd/lib/date-picker/locale/tr_TR';
import datePickerEn from 'antd/lib/date-picker/locale/en_US';
import userService from '../../../services/user/user.service';

dayjs.locale('tr');
dayjs.extend(localizedFormat);

const { Title, Text } = Typography;

const ForgottenCheckouts: React.FC = () => {
  const { t } = useTranslation();
  const [forgottenRecords, setForgottenRecords] = useState<ForgottenCheckoutDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<ForgottenCheckoutDto | null>(null);
  const [editedExitDateTime, setEditedExitDateTime] = useState<dayjs.Dayjs | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const pickerLocale = useMemo(() => {
    return i18n.language === 'tr' ? datePickerTr : datePickerEn;
  }, [i18n.language]);

  const fetchForgottenCheckouts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await userService.getForgottenCheckouts();
      setForgottenRecords(data || []);
    } catch (err: any) {
      handleApiError(err, t);
      setError(t('forgottenCheckouts.errors.fetchError', "Çıkış yapmayı unutan çalışan verileri alınırken bir hata oluştu."));
      setForgottenRecords([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchForgottenCheckouts();
  }, [fetchForgottenCheckouts]);

  const handleOpenEditModal = (record: ForgottenCheckoutDto) => {
    setSelectedRecord(record);
    const entryDateTime = dayjs(record.entryTime);
    let initialExitDateTimeSuggestion: dayjs.Dayjs;

    if (record.exitTime && dayjs(record.exitTime).isValid()) {
      const existingExitDayjs = dayjs(record.exitTime);
      // Varolan çıkış saatinin saatini ve giriş tarihinin tarihini alarak birleştir
      initialExitDateTimeSuggestion = entryDateTime
        .hour(existingExitDayjs.hour())
        .minute(existingExitDayjs.minute())
        .second(existingExitDayjs.second())
        .millisecond(existingExitDayjs.millisecond());
      
      // Eğer bir şekilde varolan çıkış saati, giriş saatinden önce veya aynıysa, 8 saat sonrasını öner
      if (initialExitDateTimeSuggestion.isSameOrBefore(entryDateTime)) {
        initialExitDateTimeSuggestion = entryDateTime.add(10, 'hour');
      }
    } else {
      // Varsayılan olarak giriş tarihinden 8 saat sonrasını öner
      initialExitDateTimeSuggestion = entryDateTime.add(10, 'hour');
    }
    setEditedExitDateTime(initialExitDateTimeSuggestion);
    setIsEditModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsEditModalVisible(false);
    setSelectedRecord(null);
    setEditedExitDateTime(null); // State'i sıfırla
  };

  const handleUpdateExitTime = async () => {
    if (!selectedRecord || !editedExitDateTime) {
      message.error(t('forgottenCheckouts.errors.selectExitDateTime', "Lütfen geçerli bir çıkış tarihi ve saati seçin."));
      return;
    }

    const originalEntryDateTime = dayjs(selectedRecord.entryTime);

    // Giriş saatinden önce bir çıkış saati seçilmesini engelle
    if (editedExitDateTime.isSameOrBefore(originalEntryDateTime)) {
      message.error(t('forgottenCheckouts.errors.exitBeforeEntry', "Çıkış tarihi ve saati, giriş tarihi ve saatinden sonra olmalıdır."));
      return;
    }
    const updatedEntryDateTime = originalEntryDateTime.add(1, 'hour');
    const updatedExitDateTime = editedExitDateTime.add(1, 'hour');

    setIsUpdating(true);
    try {
      await dailyRecordService.updateRecordTime(
        selectedRecord.recordId, 
        updatedEntryDateTime.toISOString(), // Orijinal giriş zamanı backend'e gönderilebilir
        updatedExitDateTime.toISOString() // Yeni çıkış zamanı
      );
      message.success(t('forgottenCheckouts.messages.updateSuccess', "Çıkış saati başarıyla güncellendi."));
      handleModalCancel();
      fetchForgottenCheckouts(); // Listeyi yenile
    } catch (err: any) {
      handleApiError(err, t);
    } finally {
      setIsUpdating(false);
    }
  };
  
  const formatTime = (time: string | null | undefined) => {
    if (!time) return '-';
    // const formattedTime = dayjs(time).subtract(1, 'hour');
    return dayjs(time).format('HH:mm');
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    return dayjs(date).format('DD MMMM YYYY');
  };


  const columns = [
    {
      title: t('forgottenCheckouts.table.employee', "Çalışan"),
      dataIndex: 'user',
      key: 'user',
      render: (user: ForgottenCheckoutDto['user']) => (
        <div className="d-flex align-items-center">
          <Avatar 
            src={user?.profilePicture ? getFileUrl(user.profilePicture) : user?.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'} 
            size="large" 
            className="me-3" 
          />
          <div>
            <Link to={user?.id ? all_routes.employeedetails.replace(':id', user.id.toString()) : '#'}>
              <Title level={5} style={{ marginBottom: 0 }}>{user?.firstName} {user?.lastName}</Title>
            </Link>
            <Text type="secondary">{user?.department?.name || '-'}</Text>
            <br/>
            <Text type="secondary" style={{fontSize: '12px'}}>{user?.branch?.name || '-'}</Text>
          </div>
        </div>
      ),
    },
    {
      title: t('forgottenCheckouts.table.entryTime', "Giriş Saati"),
      dataIndex: 'entryTime',
      key: 'entryTime',
      render: (text: string) => `${formatDate(text)} - ${formatTime(text)}`,
    },
    {
      title: t('forgottenCheckouts.table.exitTime', "Çıkış Saati"),
      dataIndex: 'exitTime',
      key: 'exitTime',
      render: (text: string) => `${formatDate(text)} - ${formatTime(text)}`,
    },
    {
      title: t('forgottenCheckouts.table.status', "Durum"),
      dataIndex: 'workedTime',
      key: 'workedTime',
      render: (text: string) => (
        <Tag icon={<ClockCircleOutlined />} color="warning">
          {text}
        </Tag>
      ),
    },
    {
      title: t('forgottenCheckouts.table.action', "Eylem"),
      key: 'action',
      align: 'center' as const,
      render: (_: any, record: ForgottenCheckoutDto) => (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(record)}
            size="small"
          />
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Breadcrumb */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <Title level={3} className="page-title">{t('forgottenCheckouts.pageTitle', "Çıkış yapmayı unutan çalışanlar")}</Title>
            </div>
          </div>
        </div>

        <Card bordered={false} style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
          <div className="card-body">
            {loading ? (
              <LoadingSpinner centered />
            ) : (
              <Table
                columns={columns}
                dataSource={forgottenRecords}
                rowKey="recordId"
                pagination={{ pageSize: 10, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ${t('common.table.records', 'kayıt')}` }}
                className="table-hover"
                scroll={{ x: 'max-content' }}
              />
            )}
          </div>
        </Card>

        {/* Düzenleme Modalı */}
        <Modal
          title={
            <div style={{display: 'flex', alignItems: 'center'}}>
                <ClockCircleOutlined style={{fontSize: '20px', marginRight: '10px', color: '#faad14' }}/> 
                <Title level={4} style={{marginBottom: 0}}>{t('forgottenCheckouts.modal.title', "Çıkış Saatini Güncelle")}</Title>
            </div>
          }
          open={isEditModalVisible}
          onCancel={handleModalCancel}
          destroyOnClose // Modal kapandığında içindeki state'leri sıfırla
          footer={[
            <Button key="cancel" icon={<CloseCircleOutlined />} onClick={handleModalCancel}>
              {t('common.buttons.cancel', "İptal")}
            </Button>,
            <Button
              key="submit"
              type="primary"
              icon={<SaveOutlined />}
              loading={isUpdating}
              onClick={handleUpdateExitTime}
              // disabled={!editedExitDateTime || (selectedRecord?.entryTime && editedExitDateTime.isSameOrBefore(dayjs(selectedRecord.entryTime)))}
            >
              {t('common.buttons.update', "Güncelle")}
            </Button>,
          ]}
          // width={400}
        >
          {selectedRecord && (
            <div>
              <div style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '4px' }}>
                <Title level={5} style={{marginBottom: '5px'}}>
                    {selectedRecord.user?.firstName} {selectedRecord.user?.lastName}
                </Title>
                <Text type="secondary">
                    {t('forgottenCheckouts.modal.entryOn', ` ${formatDate(selectedRecord.entryTime)} tarihinde giriş yaptı.`)}
                    <br/>
                    {t('forgottenCheckouts.modal.entryTimeLabel', "Giriş Saati:")} <strong>{formatTime(selectedRecord.entryTime)}</strong>
                </Text>
              </div>
              
              <Form layout="vertical">
              <Form.Item 
                    label={<Title level={5} style={{marginBottom: 0}}>{t('forgottenCheckouts.modal.exitDateTimeLabel', "Yeni Çıkış Tarihi ve Saati")}</Title>}
                    required
                    help={editedExitDateTime && selectedRecord?.entryTime && editedExitDateTime.isSameOrBefore(dayjs(selectedRecord.entryTime)) ? t('forgottenCheckouts.errors.exitBeforeEntry', "Çıkış, girişten sonra olmalıdır!") : null}
                    validateStatus={editedExitDateTime && selectedRecord?.entryTime && editedExitDateTime.isSameOrBefore(dayjs(selectedRecord.entryTime)) ? 'error' : ''}
                >
                  <DatePicker
                    value={editedExitDateTime}
                    onChange={setEditedExitDateTime}
                    showTime={{ format: 'HH:mm' }}
                    format="DD MMMM YYYY HH:mm"
                    style={{ width: '100%' }}
                    minuteStep={1}
                    showNow={false}
                    placeholder={t('forgottenCheckouts.modal.selectDateTimePlaceholder', "Tarih ve saat seçin")}
                    locale={pickerLocale}
                    disabledDate={(current) => {
                      if (!selectedRecord?.entryTime) return false;
                      const entryDate = dayjs(selectedRecord.entryTime).startOf('day');
                      return current && current.isBefore(entryDate);
                    }}
                    disabledTime={(current) => {
                      if (!selectedRecord?.entryTime || !current) return { disabledHours: () => [], disabledMinutes: () => [] };
                      
                      const entryDateTime = dayjs(selectedRecord.entryTime);
                      if (current.isSame(entryDateTime, 'day')) { // Eğer seçilen gün, giriş yapılan gün ile aynı ise
                        const entryHour = entryDateTime.hour();
                        const entryMinute = entryDateTime.minute();
                        return {
                          disabledHours: () => Array.from({ length: entryHour }, (_, i) => i),
                          disabledMinutes: (selectedHour) => {
                            if (selectedHour === entryHour) {
                              return Array.from({ length: entryMinute + 1 }, (_, i) => i);
                            }
                            return [];
                          },
                        };
                      }
                      return { disabledHours: () => [], disabledMinutes: () => [] }; // Farklı bir gün seçildiyse saat kısıtlaması yok (disabledDate hallediyor öncesini)
                    }}
                  />
                </Form.Item>
              </Form>
            </div>
          )}
        </Modal>
      </div>
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3 mt-auto">
        <FooterSection />
      </div>
    </div>
  );
};

export default ForgottenCheckouts;